import React, { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import {
  deleteContribution,
  getAllContributions,
  updateContribution,
} from "../../hooks/admin_api";
import { AuthContext } from "../../contexts/AuthContext";

const ContributionsList = () => {
  const { user, token } = useContext(AuthContext);

  // Role checking
  const isAdmin = () => user?.role === "admin";
  const isSuperAdmin = () => user?.role === "superadmin";
  const isStaff = () => user?.role === "staff";

  const canEditDelete = isAdmin() && !isSuperAdmin();
  const canExport = isAdmin() || isSuperAdmin();

  // State
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10); // Items per page
  
  // Search state
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    amount: "",
    category: "",
    status: "",
  });

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page on new search
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

 // Load contributions with pagination and search
const loadContributions = useCallback(async () => {
  if (!token) {
    console.log("No token available");
    return;
  }

  try {
    setLoading(true);
    setError(null);
    
    console.log(`📡 Fetching contributions - Page: ${page}, Search: "${debouncedSearch}"`);
    
    // Add a cache-busting timestamp to force fresh data
    const timestamp = Date.now();
    const res = await getAllContributions(token, page, limit, debouncedSearch);
    
    console.log("📦 API Response:", res);
    console.log("📦 Response timestamp:", timestamp);
    
    if (res?.status && Array.isArray(res.results)) {
      console.log(`✅NEW DATA Loaded ${JSON.stringify(res.results.length)} contributions`);
      console.log("✅ Contribution statuses from API:", res.results.map(c => ({ id: c.contribution_id, status: c.status })));
      
      setContributions(res.results);
      setTotal(res.total || 0);
    } else {
      console.error("Invalid response format:", res);
      setError("Failed to load contributions");
      toast.error("Failed to load contributions");
    }
  } catch (err) {
    console.error("❌ Error loading contributions:", err);
    setError(err.message);
    toast.error("Error loading contributions");
  } finally {
    setLoading(false);
  }
}, [token, page, limit, debouncedSearch]);

  // Load ALL contributions for export/print
  const loadAllContributions = async () => {
    if (!canExport) {
      toast.error("You don't have permission to export all data");
      return null;
    }

    try {
      // Use a high limit to get all records (adjust based on your data size)
      const res = await getAllContributions(token, 1, 10000, "");
      
      if (res?.status && Array.isArray(res.results)) {
        return res.results;
      } else {
        toast.error("Failed to fetch all contributions");
        return null;
      }
    } catch (err) {
      console.error("Error fetching all contributions:", err);
      toast.error("Error fetching all contributions");
      return null;
    }
  };

  // Load on mount and when dependencies change
  useEffect(() => {
    loadContributions();
  }, [loadContributions]);

  // Handle edit
  const handleEdit = (item) => {
    if (!canEditDelete) {
      toast.error("Only administrators can edit contributions");
      return;
    }
    setSelectedItem(item);
    setFormData({
      amount: item.amount,
      category: item.category,
      status: item.status,
    });
    setShowEditModal(true);
  };

  // Handle delete click
  const handleDeleteClick = (item) => {
    if (!canEditDelete) {
      toast.error("Only administrators can delete contributions");
      return;
    }
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  // Handle view
  const handleViewClick = (item) => {
    setSelectedItem(item);
    setShowViewModal(true);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!canEditDelete || !selectedItem) return;

    try {
      const res = await deleteContribution(
        selectedItem.contribution_id,
        user?.id,
        token
      );

      if (res.status || res.success) {
        toast.success(`Contribution deleted successfully`);
        loadContributions(); // Refresh list
      } else {
        toast.error(res.message || "Failed to delete contribution");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("An error occurred while deleting");
    } finally {
      setShowDeleteModal(false);
      setSelectedItem(null);
    }
  };

 // Handle save
const handleSave = async () => {
  if (!canEditDelete || !selectedItem) return;

  try {
    console.log("Saving contribution with data:", formData);
    
    // Prepare data - only send fields that are provided
    const updateData = {
      contribution_id: selectedItem.contribution_id,
    };
    
    // Only include fields that have values
    if (formData.amount) updateData.amount = formData.amount;
    if (formData.category) updateData.category = formData.category;
    if (formData.status) updateData.status = formData.status;
    
    const res = await updateContribution(
      user?.id,
      selectedItem.contribution_id,
      updateData,
      token
    );

    console.log("Update response:", res);

    // Check success from your API response format
    if (res?.success === true) {
      toast.success(res.message || "Contribution updated successfully");
      
      // Close modal and reset
      setShowEditModal(false);
      setSelectedItem(null);
      setFormData({ amount: "", category: "", status: "" });
      
      // CRITICAL FIX: Force a fresh load from the API
      // Reset to page 1 and clear search to ensure we get all data
      setPage(1);
      setSearch("");
      setDebouncedSearch("");
      
      // Add a small delay to ensure state updates complete
      setTimeout(() => {
        loadContributions();
      }, 300);
      
    } else {
      // Show error message from API
      const errorMsg = res?.error || res?.message || "Failed to update contribution";
      toast.error(errorMsg);
      console.error("Update failed:", res);
    }
  } catch (err) {
    console.error("Update error:", err);
    toast.error(err?.response?.data?.message || "Something went wrong while updating");
  }
};

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Export Current Page to Excel
  const exportToExcel = () => {
    if (!canExport || contributions.length === 0) return;

    const exportData = contributions.map(item => ({
      'ID': item.contribution_id,
      'Full Name': item.full_name,
      'Email': item.email,
      'Phone': item.phone || 'N/A',
      'Amount (₵)': parseFloat(item.amount || 0).toFixed(2),
      'Category': item.category || 'N/A',
      'Status': item.status,
      'Date Submitted': formatDate(item.created_at)
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contributions");
    
    // Auto-size columns
    const wscols = [
      { wch: 8 },  // ID
      { wch: 25 }, // Full Name
      { wch: 30 }, // Email
      { wch: 15 }, // Phone
      { wch: 12 }, // Amount
      { wch: 15 }, // Category
      { wch: 12 }, // Status
      { wch: 20 }, // Date
    ];
    ws['!cols'] = wscols;

    XLSX.writeFile(wb, `contributions_page_${page}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(`Exported page ${page} (${contributions.length} contributions)`);
  };

  // Export ALL Contributions to Excel
  const exportAllToExcel = async () => {
    if (!canExport) {
      toast.error("You don't have permission to export all data");
      return;
    }

    try {
      setExportLoading(true);
      toast.info("Fetching all contributions for export...");

      const allData = await loadAllContributions();
      
      if (allData && allData.length > 0) {
        // Prepare data for export
        const exportData = allData.map(item => ({
          'ID': item.contribution_id,
          'Full Name': item.full_name,
          'Email': item.email,
          'Phone': item.phone || 'N/A',
          'Amount (₵)': parseFloat(item.amount || 0).toFixed(2),
          'Category': item.category || 'N/A',
          'Status': item.status,
          'Date Submitted': formatDate(item.created_at)
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "All Contributions");
        
        // Auto-size columns
        const wscols = [
          { wch: 8 },  // ID
          { wch: 25 }, // Full Name
          { wch: 30 }, // Email
          { wch: 15 }, // Phone
          { wch: 12 }, // Amount
          { wch: 15 }, // Category
          { wch: 12 }, // Status
          { wch: 20 }, // Date
        ];
        ws['!cols'] = wscols;

        const fileName = `all_contributions_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
        toast.success(`Successfully exported all ${allData.length} contributions`);
      } else {
        toast.warning("No contributions found to export");
      }
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export all contributions");
    } finally {
      setExportLoading(false);
    }
  };

  // Print ALL Contributions
  const printAllContributions = async () => {
    if (!canExport) {
      toast.error("You don't have permission to print all data");
      return;
    }

    try {
      setPrintLoading(true);
      toast.info("Preparing print view...");

      const allData = await loadAllContributions();
      
      if (allData && allData.length > 0) {
        // Calculate summary statistics
        const totalAmount = allData.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
        const pendingCount = allData.filter(item => item.status === 'PENDING').length;
        const approvedCount = allData.filter(item => item.status === 'APPROVED').length;
        const rejectedCount = allData.filter(item => item.status === 'REJECTED').length;

        // Create print window
        const printWindow = window.open('', '_blank', 'width=1200,height=800');
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>All Contributions Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #4CAF50; padding-bottom: 20px; }
              .title { color: #4CAF50; margin: 0; }
              .summary { display: flex; justify-content: space-around; margin-bottom: 30px; }
              .summary-card { text-align: center; padding: 15px; background: #f8f9fa; border-radius: 8px; min-width: 150px; }
              .summary-label { font-size: 14px; color: #666; }
              .summary-value { font-size: 24px; font-weight: bold; margin-top: 5px; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
              th { background-color: #4CAF50; color: white; padding: 12px; text-align: left; }
              td { padding: 8px; border-bottom: 1px solid #ddd; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .status-badge { padding: 4px 8px; border-radius: 4px; color: white; font-size: 11px; font-weight: bold; }
              .status-pending { background-color: #ffc107; color: #000; }
              .status-approved { background-color: #28a745; }
              .status-rejected { background-color: #dc3545; }
              .footer { margin-top: 30px; text-align: center; color: #666; font-size: 12px; border-top: 1px solid #ddd; padding-top: 20px; }
              @media print { .no-print { display: none; } }
            </style>
          </head>
          <body>
            <div class="header">
              <h1 class="title">Contributions Report</h1>
              <p>Generated on: ${new Date().toLocaleString()}</p>
              <p>Total Records: ${allData.length}</p>
            </div>

            <div class="summary">
              <div class="summary-card">
                <div class="summary-label">Total Amount</div>
                <div class="summary-value">₵${totalAmount.toFixed(2)}</div>
              </div>
              <div class="summary-card" style="background: #fff3cd;">
                <div class="summary-label">Pending</div>
                <div class="summary-value">${pendingCount}</div>
              </div>
              <div class="summary-card" style="background: #d4edda;">
                <div class="summary-label">Approved</div>
                <div class="summary-value">${approvedCount}</div>
              </div>
              <div class="summary-card" style="background: #f8d7da;">
                <div class="summary-label">Rejected</div>
                <div class="summary-value">${rejectedCount}</div>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Amount</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                ${allData.map(item => `
                  <tr>
                    <td>#${item.contribution_id}</td>
                    <td><strong>${item.full_name}</strong></td>
                    <td>${item.email}</td>
                    <td>${item.phone || 'N/A'}</td>
                    <td><strong>₵${parseFloat(item.amount || 0).toFixed(2)}</strong></td>
                    <td>${item.category || 'N/A'}</td>
                    <td>
                      <span class="status-badge status-${item.status.toLowerCase()}">
                        ${item.status}
                      </span>
                    </td>
                    <td>${formatDate(item.created_at)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>

            <div class="footer">
              <p>This is an official report generated by the welfare system.</p>
              <p class="no-print">
                <button onclick="window.print()" style="padding: 10px 20px; background: #4CAF50; color: white; border: none; border-radius: 5px; cursor: pointer;">
                  Print Report
                </button>
              </p>
            </div>
          </body>
          </html>
        `);
        
        printWindow.document.close();
        toast.success(`Print view prepared with ${allData.length} contributions`);
      } else {
        toast.warning("No contributions found to print");
      }
    } catch (err) {
      console.error("Print error:", err);
      toast.error("Failed to prepare print view");
    } finally {
      setPrintLoading(false);
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="container-fluid mt-3">
      {/* Header with all export buttons */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h4 className="fw-bold mb-0">
          Contributions 
          <span className="badge bg-secondary ms-2">{total}</span>
        </h4>
        
        {canExport && (
          <div className="btn-group">
            <button
              className="btn btn-success"
              onClick={exportToExcel}
              disabled={contributions.length === 0}
              title="Export current page"
            >
              <i className="bi bi-file-earmark-excel me-2"></i>
              Export Page
            </button>
            
            <button
              className="btn btn-warning"
              onClick={exportAllToExcel}
              disabled={exportLoading}
              title="Export all contributions"
            >
              {exportLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Exporting...
                </>
              ) : (
                <>
                  <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                  Export All
                </>
              )}
            </button>
            
            <button
              className="btn btn-danger"
              onClick={printAllContributions}
              disabled={printLoading}
              title="Print all contributions"
            >
              {printLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Preparing...
                </>
              ) : (
                <>
                  <i className="bi bi-printer me-2"></i>
                  Print All
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, email, or category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                className="btn btn-outline-secondary"
                onClick={() => setSearch("")}
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
        </div>
        <div className="col-md-6 text-md-end">
          <small className="text-muted">
            Showing {contributions.length} of {total} records
          </small>
        </div>
      </div>

      {/* Simple Status Summary */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card bg-light">
            <div className="card-body">
              <h6>Total</h6>
              <h3>{total}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-warning text-dark">
            <div className="card-body">
              <h6>Pending</h6>
              <h3>{contributions.filter(c => c.status === 'PENDING').length}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-success text-white">
            <div className="card-body">
              <h6>Approved</h6>
              <h3>{contributions.filter(c => c.status === 'APPROVED').length}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-danger text-white">
            <div className="card-body">
              <h6>Rejected</h6>
              <h3>{contributions.filter(c => c.status === 'REJECTED').length}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Amount</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : contributions.length > 0 ? (
                  contributions.map((item) => (
                    <tr key={item.contribution_id}>
                      <td>#{item.contribution_id}</td>
                      <td>
                        <div>
                          <strong>{item.full_name}</strong>
                          <br />
                          <small className="text-muted">{item.email}</small>
                        </div>
                      </td>
                      <td>
                        <strong>₵{parseFloat(item.amount || 0).toFixed(2)}</strong>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark">
                          {item.category || "N/A"}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${
                          item.status === "PENDING" ? "bg-warning text-dark" :
                          item.status === "APPROVED" ? "bg-success" : "bg-danger"
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td>
                        <small>{formatDate(item.created_at)}</small>
                      </td>
                      <td>
                        <div className="d-flex justify-content-center gap-2">
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => handleViewClick(item)}
                            title="View"
                          >
                            <i className="bi bi-eye"></i>
                          </button>

                          {canEditDelete && (
                            <>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleEdit(item)}
                                title="Edit"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteClick(item)}
                                title="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <p className="text-muted mb-0">No contributions found</p>
                      {search && <p className="text-muted small">Try adjusting your search</p>}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div>
            <small className="text-muted">
              Page {page} of {totalPages}
            </small>
          </div>
          <div className="btn-group">
            <button
              className="btn btn-outline-primary"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <i className="bi bi-chevron-left"></i> Previous
            </button>
            <button
              className="btn btn-outline-primary"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next <i className="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedItem && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">Contribution Details</h5>
                <button className="btn-close btn-close-white" onClick={() => setShowViewModal(false)}></button>
              </div>
              <div className="modal-body">
                <p><strong>ID:</strong> #{selectedItem.contribution_id}</p>
                <p><strong>Name:</strong> {selectedItem.full_name}</p>
                <p><strong>Email:</strong> {selectedItem.email}</p>
                <p><strong>Phone:</strong> {selectedItem.phone || 'N/A'}</p>
                <p><strong>Amount:</strong> ₵{parseFloat(selectedItem.amount || 0).toFixed(2)}</p>
                <p><strong>Category:</strong> {selectedItem.category || 'N/A'}</p>
                <p><strong>Status:</strong> 
                  <span className={`badge ms-2 ${
                    selectedItem.status === "PENDING" ? "bg-warning" :
                    selectedItem.status === "APPROVED" ? "bg-success" : "bg-danger"
                  }`}>
                    {selectedItem.status}
                  </span>
                </p>
                <p><strong>Date:</strong> {formatDate(selectedItem.created_at)}</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
                {canEditDelete && (
                  <>
                    <button 
                      className="btn btn-primary" 
                      onClick={() => {
                        setShowViewModal(false);
                        handleEdit(selectedItem);
                      }}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-danger" 
                      onClick={() => {
                        setShowViewModal(false);
                        handleDeleteClick(selectedItem);
                      }}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedItem && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Contribution #{selectedItem.contribution_id}</h5>
                <button className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Amount (₵)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Category</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedItem && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">Delete Contribution</h5>
                <button className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this contribution?</p>
                <p className="fw-bold">#{selectedItem.contribution_id} - ₵{parseFloat(selectedItem.amount || 0).toFixed(2)}</p>
                <p className="text-muted small">{selectedItem.full_name} • {selectedItem.email}</p>
                <p className="text-danger small">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bootstrap Icons */}
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css" />
    </div>
  );
};

export default ContributionsList;